import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Alert, ScrollView, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useUser } from '../../../../context/UserContext';
import FeedbackModal from '../../../Common/FeedbackModal';
import { BACKEND_URL } from '../../../../Config/Const';
import { colores, fuentes } from '../../../../styles/variablesCss';

const CambiarContra = forwardRef((props, ref) => {
  const { codUsu, mail } = useUser();
  const [contraActual, setContraActual] = useState('');
  const [contraNueva, setContraNueva] = useState('');
  const [repetirContra, setRepetirContra] = useState('');
  const didPrefillRef = useRef(false);
  const [showPassword, setShowPassword] = useState({
    actual: false,
    nueva: false,
    repetir: false
  });

  const decodeEntities = (str = '') =>
    String(str)
      .replace(/&ntilde;/g, 'ñ')
      .replace(/&Ntilde;/g, 'Ñ')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

  // Estados para el modal de feedback
  const [feedbackModal, setFeedbackModal] = useState({
    visible: false,
    type: 'success',
    title: '',
    message: ''
  });

  useEffect(() => {
    if (didPrefillRef.current) return;
    if (props && props.prefillContraActual && String(props.prefillContraActual).trim() !== '') {
      setContraActual(String(props.prefillContraActual));
      didPrefillRef.current = true;
    }
  }, [props && props.prefillContraActual]);

  // Exponemos el método handleCambiarContra para que pueda ser llamado desde el componente padre
  useImperativeHandle(ref, () => ({
    handleCambiarContra
  }));

  const limpiarCampos = () => {
    setContraActual('');
    setContraNueva('');
    setRepetirContra('');
    setShowPassword({
      actual: false,
      nueva: false,
      repetir: false
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleCambiarContra = async () => {
    if (!contraActual) {
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'Por favor ingrese su contraseña actual'
      });
      return;
    }

    if (!contraNueva || !repetirContra) {
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'Por favor complete todos los campos'
      });
      return;
    }

    if (contraNueva !== repetirContra) {
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'Las contraseñas nuevas no coinciden'
      });
      return;
    }

    try {
      const body = JSON.stringify({
        "request": {
          codUsu: codUsu,
          mail: mail,
          cont: contraNueva,
          cont2: repetirContra,
          contActual: contraActual,
        }
      });

      const response = await fetch(`${BACKEND_URL}/rest/usuarios/cambiar-clave`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body
      });

      const contentType = response.headers.get('content-type') || '';
      let data = null;
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        try { data = JSON.parse(text); } catch { data = null; }
      }

      if (!response.ok) {
        const apiMsg = decodeEntities(
          data && data.dsResultado && data.dsResultado.resultado && data.dsResultado.resultado[0]
            ? data.dsResultado.resultado[0].mensaje || ''
            : ''
        );
        setFeedbackModal({
          visible: true,
          type: 'error',
          title: 'Error',
          message: apiMsg || 'No se pudieron actualizar los datos'
        });
        return;
      }

      const logicalOk = !!(data && data.dsResultado && data.dsResultado.resultado && data.dsResultado.resultado[0] && data.dsResultado.resultado[0].resultado);
      if (logicalOk) {
        setFeedbackModal({
          visible: true,
          type: 'success',
          title: 'Éxito',
          message: 'La contraseña se actualizó correctamente'
        });
        limpiarCampos();
      } else {
        const apiMsg = decodeEntities(
          data && data.dsResultado && data.dsResultado.resultado && data.dsResultado.resultado[0]
            ? data.dsResultado.resultado[0].mensaje || ''
            : ''
        );
        setFeedbackModal({
          visible: true,
          type: 'error',
          title: 'Error',
          message: apiMsg || 'No se pudieron actualizar los datos'
        });
      }
    } catch (error) {
      console.error('Error al modificar usuario:', error);
      setFeedbackModal({
        visible: true,
        type: 'error',
        title: 'Error',
        message: 'Ocurrió un error al intentar actualizar los datos'
      });
    }
    finally {
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View style={styles.formContainer}>
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Contraseña Actual:</Text>
            <View style={styles.passwordPillContainer}>
              <TextInput
                style={styles.passwordInput}
                value={contraActual}
                onChangeText={setContraActual}
                secureTextEntry={!showPassword.actual}
                placeholder="Ingrese su contraseña actual"
                placeholderTextColor="#A0A0A0"
                autoComplete="current-password"
                textContentType="password"
                autoCapitalize="none"
              />
              <TouchableOpacity
                style={styles.eyeIcon}
                onPress={() => togglePasswordVisibility('actual')}
                hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
              >
                <MaterialIcons
                  name={showPassword.actual ? "visibility" : "visibility-off"}
                  size={22}
                  color="#666"
                />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Nueva Contraseña:</Text>
            <View style={styles.passwordPillContainer}>
              <TextInput
                style={styles.passwordInput}
                value={contraNueva}
                onChangeText={setContraNueva}
                secureTextEntry={!showPassword.nueva}
                placeholder="Ingrese su nueva contraseña"
                placeholderTextColor="#A0A0A0"
                autoComplete="new-password"
                textContentType="newPassword"
                autoCapitalize="none"
              />
              <TouchableOpacity
                style={styles.eyeIcon}
                onPress={() => togglePasswordVisibility('nueva')}
                hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
              >
                <MaterialIcons
                  name={showPassword.nueva ? "visibility" : "visibility-off"}
                  size={22}
                  color="#666"
                />
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Repetir Nueva Contraseña:</Text>
            <View style={styles.passwordPillContainer}>
              <TextInput
                style={styles.passwordInput}
                value={repetirContra}
                onChangeText={setRepetirContra}
                secureTextEntry={!showPassword.repetir}
                placeholder="Repita su nueva contraseña"
                placeholderTextColor="#A0A0A0"
                autoComplete="new-password"
                textContentType="newPassword"
                autoCapitalize="none"
              />
              <TouchableOpacity
                style={styles.eyeIcon}
                onPress={() => togglePasswordVisibility('repetir')}
                hitSlop={{ top: 6, right: 6, bottom: 6, left: 6 }}
              >
                <MaterialIcons
                  name={showPassword.repetir ? "visibility" : "visibility-off"}
                  size={22}
                  color="#666"
                />
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Acción: Guardar cambios */}
        <View style={styles.actionsRow}>
          <TouchableOpacity style={styles.saveButton} onPress={handleCambiarContra}>
            <Text style={styles.saveButtonText}>Guardar</Text>
          </TouchableOpacity>
        </View>
      </ScrollView >
      
      {/* Modal de feedback */}
      <FeedbackModal
        visible={feedbackModal.visible}
        type={feedbackModal.type}
        title={feedbackModal.title}
        message={feedbackModal.message}
        onClose={() => setFeedbackModal({ ...feedbackModal, visible: false })}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colores.blanco,
  },
  formContainer: {
    padding: 20,
    maxWidth: '100%',
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    color: colores.texto,
    marginBottom: 8,
    fontFamily: fuentes.medium,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 8,
    backgroundColor: colores.blanco,
  },
  // Nuevo estilo pill para inputs de contraseña
  passwordPillContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E5E5E5',
    backgroundColor: colores.blanco,
    borderRadius: 21,
    paddingHorizontal: 10,
  },
  input: {
    flex: 1,
    height: 45,
    paddingHorizontal: 15,
  },
  passwordInput: {
    flex: 1,
    height: 42,
    paddingHorizontal: 12,
  },
  eyeIcon: {
    padding: 10,
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 10,
  },
  saveButton: {
    backgroundColor: 'red',
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 20,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }
      : {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      }
    ),
  },
  saveButtonText: {
    color: colores.blanco,
    fontFamily: fuentes.bold,
    fontSize: 14,
  },
});

export default CambiarContra;